import type { ReactElement, ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';
import * as React from 'react';

import type { Action as SuggestedAction } from 'bundles/ai-coach-platform/components/patterns/popover/types';

export type ChatMode = 'conversation' | 'search';

export const DEFAULT_CHAT_MODE: ChatMode = 'conversation';

type CoachChatContextType = {
  // Chat mode management
  chatMode: ChatMode;
  setChatMode: (mode: ChatMode) => void;
  isConversationMode: () => boolean;

  // Find-in-chat search functionality
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  clearSearch: () => void;
  isSearchMode: () => boolean;
  enterSearchMode: (searchTerm?: string) => void;
  exitSearchMode: () => void;

  reportSearchTerm: (ref: React.RefObject<HTMLElement>) => void;
  activeResultIndex: number | null;
  setActiveResultIndex: (index: number | null) => void;
  results: (React.RefObject<HTMLElement> | undefined)[];

  // Suggested actions management
  suggestedActions?: SuggestedAction[];
  setSuggestedActions?: (actions: SuggestedAction[]) => void;

  // Message pagination management
  earlierMessagesAvailable: boolean;
  setEarlierMessagesAvailable: (available: boolean) => void;
  disableProgrammaticScroll: boolean;
  setDisableProgrammaticScroll: (disabled: boolean) => void;
  chatHistoryCursor: string | null;
  setChatHistoryCursor: (cursor: string | null) => void;
  chatHistoryLimit: number;
  setChatHistoryLimit: (limit: number) => void;

  chatPrefixContent: ReactElement | null;
  setChatPrefixContent: (content: ReactElement | null) => void;

  // Settings panel management
  settingsOpen: boolean;
  openSettings: () => void;
  closeSettings: () => void;

  // Whether logout mode is enabled (for passing deviceId to queries)
  enableLoggedOutMode: boolean;
};

const CoachChatContext = createContext<CoachChatContextType>({
  chatMode: DEFAULT_CHAT_MODE,
  setChatMode: () => {},
  isConversationMode: () => false,
  searchTerm: '',
  setSearchTerm: () => {},
  clearSearch: () => {},
  isSearchMode: () => false,
  enterSearchMode: () => {},
  exitSearchMode: () => {},
  reportSearchTerm: () => {},
  activeResultIndex: null,
  setActiveResultIndex: () => {},
  results: [],
  suggestedActions: [],
  setSuggestedActions: undefined,
  earlierMessagesAvailable: false,
  setEarlierMessagesAvailable: () => {},
  disableProgrammaticScroll: false,
  setDisableProgrammaticScroll: () => {},
  chatHistoryCursor: null,
  setChatHistoryCursor: () => {},
  chatHistoryLimit: 5,
  setChatHistoryLimit: () => {},
  chatPrefixContent: null,
  setChatPrefixContent: () => {},
  settingsOpen: false,
  openSettings: () => {},
  closeSettings: () => {},
  enableLoggedOutMode: false,
});

type CoachChatContextProviderProps = {
  children: ReactNode;
  enableLoggedOutMode?: boolean;
};

export const CoachChatContextProvider = ({ children, enableLoggedOutMode = false }: CoachChatContextProviderProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [chatMode, setChatMode] = useState<ChatMode>(DEFAULT_CHAT_MODE);
  const [activeResultIndex, setActiveResultIndex] = useState<number | null>(null);
  const [results, setResults] = useState<(React.RefObject<HTMLElement> | undefined)[]>([]);
  const [suggestedActions, setSuggestedActions] = useState<SuggestedAction[]>([]);
  const [earlierMessagesAvailable, setEarlierMessagesAvailable] = useState<boolean>(false);
  const [disableProgrammaticScroll, setDisableProgrammaticScroll] = useState<boolean>(false);
  const [chatHistoryCursor, setChatHistoryCursor] = useState<string | null>(null);
  const [chatHistoryLimit, setChatHistoryLimit] = useState<number>(5);
  const [chatPrefixContent, setChatPrefixContent] = useState<ReactElement | null>(null);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);

  const openSettings = useCallback<CoachChatContextType['openSettings']>(() => {
    setSettingsOpen(true);
  }, []);

  const closeSettings = useCallback<CoachChatContextType['closeSettings']>(() => {
    setSettingsOpen(false);
  }, []);

  const enterSearchMode = useCallback<CoachChatContextType['enterSearchMode']>((initialSearchTerm = '') => {
    setChatMode('search');

    if (initialSearchTerm) {
      setSearchTerm(initialSearchTerm);
    }
  }, []);

  const clearSearch = useCallback<CoachChatContextType['clearSearch']>(() => {
    setSearchTerm('');
    setResults([]);
    setActiveResultIndex(null);
  }, []);

  const exitSearchMode = useCallback<CoachChatContextType['exitSearchMode']>(() => {
    setChatMode(DEFAULT_CHAT_MODE);
    clearSearch();
  }, [clearSearch]);

  const reportSearchTerm = useCallback<CoachChatContextType['reportSearchTerm']>(
    (ref: React.RefObject<HTMLElement>) => {
      setResults((prevResults) => [...prevResults, ref]);
    },
    []
  );

  const handleSetSearchTerm = useCallback<CoachChatContextType['setSearchTerm']>(
    (newTerm) => {
      if (newTerm !== searchTerm) {
        // clear current term and results before updating
        clearSearch();
        setSearchTerm(newTerm);
      }
    },
    [searchTerm, clearSearch, setSearchTerm]
  );

  const contextValue: CoachChatContextType = {
    chatMode,
    setChatMode,
    isConversationMode: () => chatMode === 'conversation',
    searchTerm,
    setSearchTerm: handleSetSearchTerm,
    clearSearch,
    isSearchMode: () => chatMode === 'search',
    enterSearchMode,
    exitSearchMode,
    reportSearchTerm,
    activeResultIndex,
    setActiveResultIndex,
    results,
    suggestedActions,
    setSuggestedActions,
    earlierMessagesAvailable,
    setEarlierMessagesAvailable,
    disableProgrammaticScroll,
    setDisableProgrammaticScroll,
    chatHistoryCursor,
    setChatHistoryCursor,
    chatHistoryLimit,
    setChatHistoryLimit,
    chatPrefixContent,
    setChatPrefixContent,
    settingsOpen,
    openSettings,
    closeSettings,
    enableLoggedOutMode,
  };

  return <CoachChatContext.Provider value={contextValue}>{children}</CoachChatContext.Provider>;
};

export const useCoachChatContext = (): CoachChatContextType => {
  const context = useContext(CoachChatContext);
  return context;
};
